/**
 * Stage Card Renderers
 *
 * Visual cards for displaying intermediate results from each optimization
 * pipeline stage: baseline evaluation, failure insights, prompt mutations,
 * and re-run accuracy comparison.
 */

import chalk from 'chalk';
import { generatePromptDiff, renderDiffLines } from './prompt-diff.js';

// =============================================================================
// Types
// =============================================================================

export interface BaselineEvalData {
  overallScore: number;
  scores: { name: string; score: number }[];
  passCount: number;
  failCount: number;
  totalItems: number;
  duration: number;
}

export interface InsightsData {
  categories: { name: string; severity: string; count: number; example?: string }[];
  totalFailures: number;
  severityDistribution: Record<string, number>;
}

export interface PromptDiffData {
  originalPrompt: string;
  mutatedPrompt: string;
  mutations: { target: string; status: string; confidence: number; rationale?: string }[];
}

export interface RerunAccuracyData {
  baselineScore: number;
  newScore: number;
  delta: number;
  perCriterion: { name: string; before: number; after: number }[];
  improvedItems: number;
  regressedItems: number;
  unchangedItems: number;
}

// =============================================================================
// Shared Helpers
// =============================================================================

// eslint-disable-next-line no-control-regex
const ANSI_REGEX = /\u001B\[[0-9;]*m/g;

const BOX_WIDTH = 66;

function stripAnsi(text: string): string {
  return text.replace(ANSI_REGEX, '');
}

function pad(text: string, width: number): string {
  const stripped = stripAnsi(text);
  const remaining = width - stripped.length;
  return remaining > 0 ? text + ' '.repeat(remaining) : text;
}

function line(text: string): string {
  return `\u2502 ${pad(text, BOX_WIDTH - 2)} \u2502`;
}

function topBorder(title: string): string {
  const titleStr = ` ${title} `;
  const remaining = BOX_WIDTH - titleStr.length - 1;
  return `\u250C\u2500${titleStr}${'\u2500'.repeat(Math.max(0, remaining))}\u2510`;
}

function bottomBorder(): string {
  return `\u2514${'\u2500'.repeat(BOX_WIDTH)}\u2518`;
}

function emptyLine(): string {
  return line('');
}

function formatScore(score: number): string {
  if (score >= 0.8) return chalk.green(score.toFixed(2));
  if (score >= 0.5) return chalk.yellow(score.toFixed(2));
  return chalk.red(score.toFixed(2));
}

function scoreBar(score: number, width = 20): string {
  const filled = Math.round(score * width);
  return '\u2588'.repeat(filled) + '\u2591'.repeat(width - filled);
}

// =============================================================================
// Baseline Evaluation Card
// =============================================================================

export function renderBaselineEvalCard(data: BaselineEvalData): string {
  const lines: string[] = [];

  lines.push(topBorder('\uD83D\uDCCA Baseline Evaluation'));
  lines.push(line(`Overall Score: ${formatScore(data.overallScore)}`));
  lines.push(emptyLine());

  // Criterion table header
  lines.push(line(`${'Criterion'.padEnd(20)}${'Score'.padEnd(8)}`));
  lines.push(line('\u2500'.repeat(40)));

  for (const criterion of data.scores) {
    const name = criterion.name.length > 18
      ? criterion.name.substring(0, 15) + '...'
      : criterion.name;
    const scoreStr = formatScore(criterion.score);
    const bar = scoreBar(criterion.score);
    lines.push(line(`${name.padEnd(20)}${scoreStr}   ${bar}`));
  }

  lines.push(emptyLine());

  const durationStr = data.duration < 1
    ? `${(data.duration * 1000).toFixed(0)}ms`
    : `${data.duration.toFixed(1)}s`;
  const footer = `Dataset: ${String(data.totalItems)} items \u2502 Pass: ${String(data.passCount)} \u2502 Fail: ${String(data.failCount)} \u2502 Duration: ${durationStr}`;
  lines.push(line(footer));
  lines.push(bottomBorder());

  return lines.join('\n');
}

// =============================================================================
// Failure Insights Card
// =============================================================================

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

function severityRank(severity: string): number {
  return SEVERITY_ORDER[severity.toLowerCase()] ?? 4;
}

export function renderInsightsCard(data: InsightsData): string {
  const lines: string[] = [];

  // Sort categories by severity (critical first)
  const sorted = [...data.categories].sort(
    (a, b) => severityRank(a.severity) - severityRank(b.severity),
  );

  const categoryCount = sorted.length;
  lines.push(topBorder('\uD83D\uDD0D Failure Insights'));
  lines.push(line(`${String(data.totalFailures)} failures across ${String(categoryCount)} categories`));
  lines.push(emptyLine());

  for (const cat of sorted) {
    const itemWord = cat.count === 1 ? 'item' : 'items';
    const label = `\u25A0 ${cat.name} (${cat.severity})`;
    const countStr = `${String(cat.count)} ${itemWord}`;
    // Pad between label and count with dashes
    const labelLen = stripAnsi(label).length;
    const countLen = countStr.length;
    const dashCount = Math.max(2, BOX_WIDTH - 4 - labelLen - countLen - 2);
    lines.push(line(`${label} ${'\u2500'.repeat(dashCount)} ${countStr}`));

    if (cat.example) {
      const maxExampleLen = 78;
      const truncated = cat.example.length > maxExampleLen
        ? cat.example.substring(0, maxExampleLen - 3) + '...'
        : cat.example;
      lines.push(line(chalk.dim(`  "${truncated}"`)));
    }
  }

  lines.push(emptyLine());

  // Severity distribution footer
  const parts: string[] = [];
  const orderedKeys = ['critical', 'high', 'medium', 'low'];
  for (const key of orderedKeys) {
    const count = data.severityDistribution[key];
    if (count !== undefined && count > 0) {
      parts.push(`${String(count)} ${key}`);
    }
  }
  // Include any non-standard severity keys
  for (const [key, count] of Object.entries(data.severityDistribution)) {
    if (!orderedKeys.includes(key) && count > 0) {
      parts.push(`${String(count)} ${key}`);
    }
  }
  lines.push(line(`Severity: ${parts.join(' \u2502 ')}`));
  lines.push(bottomBorder());

  return lines.join('\n');
}

// =============================================================================
// Prompt Diff Card
// =============================================================================

export function renderPromptDiffCard(data: PromptDiffData): string {
  const lines: string[] = [];

  const applied = data.mutations.filter(m => m.status === 'applied');
  const rejected = data.mutations.filter(m => m.status !== 'applied');
  const appliedCount = applied.length;
  const rejectedCount = rejected.length;

  const summary = rejectedCount > 0
    ? `${String(appliedCount)} mutations applied, ${String(rejectedCount)} rejected`
    : `${String(appliedCount)} mutations applied`;

  lines.push(topBorder('\u270F\uFE0F Prompt Mutations Applied'));
  lines.push(line(summary));
  lines.push(emptyLine());

  // Generate diff using prompt-diff utility
  const diffLines = generatePromptDiff(data.originalPrompt, data.mutatedPrompt);
  const rendered = renderDiffLines(diffLines, { maxLines: 20 });

  if (rendered) {
    for (const diffLine of rendered.split('\n')) {
      lines.push(line(` ${diffLine}`));
    }
    lines.push(emptyLine());
  }

  // Mutation status list
  for (const mutation of data.mutations) {
    const icon = mutation.status === 'applied' ? chalk.green('\u2713') : chalk.red('\u2717');
    const target = mutation.target.length > 30
      ? mutation.target.substring(0, 27) + '...'
      : mutation.target;

    if (mutation.status === 'applied') {
      const confStr = `confidence: ${mutation.confidence.toFixed(2)}`;
      const targetPad = target.padEnd(30);
      lines.push(line(`${icon} ${targetPad} ${chalk.dim('\u2500\u2500')} ${confStr}`));
    } else {
      const reason = mutation.rationale
        ? `rejected: ${mutation.rationale}`
        : `rejected`;
      const truncReason = reason.length > 30 ? reason.substring(0, 27) + '...' : reason;
      const targetPad = target.padEnd(30);
      lines.push(line(`${icon} ${targetPad} ${chalk.dim('\u2500\u2500')} ${truncReason}`));
    }
  }

  lines.push(emptyLine());

  // Line count footer
  const origLines = data.originalPrompt.split('\n').length;
  const mutLines = data.mutatedPrompt.split('\n').length;
  const netChange = mutLines - origLines;
  const netStr = netChange >= 0 ? `+${String(netChange)}` : String(netChange);
  lines.push(line(`Net change: ${netStr} lines \u2502 Original: ${String(origLines)} \u2192 Mutated: ${String(mutLines)} lines`));
  lines.push(bottomBorder());

  return lines.join('\n');
}

// =============================================================================
// Re-run Accuracy Card
// =============================================================================

export function renderRerunAccuracyCard(data: RerunAccuracyData): string {
  const lines: string[] = [];

  const pctChange = data.baselineScore > 0
    ? ((data.delta / data.baselineScore) * 100).toFixed(1)
    : '0.0';
  const sign = data.delta >= 0 ? '+' : '';
  const deltaStr = `${sign}${data.delta.toFixed(2)}, ${sign}${pctChange}%`;

  // Score bar in top line
  const bar = scoreBar(data.newScore, 10);
  const scoreTransition = `Score: ${formatScore(data.baselineScore)} \u2192 ${formatScore(data.newScore)}  (${deltaStr})`;

  lines.push(topBorder('\uD83C\uDFAF Re-run Results'));
  lines.push(line(`${scoreTransition}${' '.repeat(Math.max(0, 4))}${bar}`));
  lines.push(emptyLine());

  // Per-criterion table
  lines.push(line(`${'Criterion'.padEnd(20)}${'Before'.padEnd(9)}${'After'.padEnd(9)}${'Delta'.padEnd(10)}`));
  lines.push(line('\u2500'.repeat(50)));

  for (const criterion of data.perCriterion) {
    const name = criterion.name.length > 18
      ? criterion.name.substring(0, 15) + '...'
      : criterion.name;
    const diff = criterion.after - criterion.before;
    const diffSign = diff >= 0 ? '+' : '';
    const arrow = diff > 0 ? chalk.green('\u2191') : diff < 0 ? chalk.red('\u2193') : chalk.dim('\u2500');
    const diffStr = `${diffSign}${diff.toFixed(2)}`;

    lines.push(line(
      `${name.padEnd(20)}${formatScore(criterion.before).padEnd(9)}${formatScore(criterion.after).padEnd(9)}${diffStr}  ${arrow}`,
    ));
  }

  lines.push(emptyLine());

  const itemsSummary = `Items: ${String(data.improvedItems)} improved \u2502 ${String(data.regressedItems)} regressed \u2502 ${String(data.unchangedItems)} unchanged`;
  lines.push(line(itemsSummary));
  lines.push(bottomBorder());

  return lines.join('\n');
}

// =============================================================================
// Dispatcher
// =============================================================================

export function renderStageCard(stage: string, data: unknown): string {
  switch (stage) {
    case 'baseline-eval':
      return renderBaselineEvalCard(data as BaselineEvalData);
    case 'insights':
      return renderInsightsCard(data as InsightsData);
    case 'prompt-diff':
      return renderPromptDiffCard(data as PromptDiffData);
    case 'rerun-accuracy':
      return renderRerunAccuracyCard(data as RerunAccuracyData);
    default:
      return `Unknown stage: ${stage}`;
  }
}
